import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Typography, TablePagination, TableSortLabel, TextField, Box,
  InputAdornment, Paper, IconButton, Divider, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from "xlsx";
import DownloadIcon from '@mui/icons-material/Download';
import api from "../api";
import AgregarVehiculo from "./utilities/AgregarVehiculo"; // Modal for adding a vehicle
import DetalleVehiculo from "./utilities/DetalleVehiculo";
import EditarVehiculo from "./utilities/EditarVehiculo"; // Optional: modal for editing a vehicle

const Vehiculos = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [open, setOpen] = useState(false); // For add vehicle modal
  const [openEdit, setOpenEdit] = useState(false); // For edit vehicle modal
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehiculo, setSelectedVehiculo] = useState(null); // For detail modal
  const [anchorEl, setAnchorEl] = useState(null); // For row dropdown menu
  const [menuVehiculo, setMenuVehiculo] = useState(null); // Vehicle for which the menu is open

  useEffect(() => {
    fetchVehiculos();
  }, []);

  const fetchVehiculos = () => {
    api.get("vehiculos/")
      .then((response) => {
        setVehiculos(response.data);
      })
      .catch((error) => {
        console.error("Error al obtener vehículos:", error);
      });
  };

  // Delete vehicle action
  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este vehículo?")) {
      api.delete(`vehiculos/${id}/`)
        .then(() => {
          setVehiculos(vehiculos.filter(v => v.id !== id));
          handleMenuClose();
        })
        .catch(error => {
          console.error("Error al eliminar el vehículo:", error);
        });
    }
  };

  const handleVehiculosUpdated = (updatedVehiculos) => {
    setVehiculos((prevVehiculos) => {
      const exists = prevVehiculos.some((i) => i.id === updatedVehiculos.id);
      if (exists) {
        return prevVehiculos.map((i) =>
          i.id === updatedVehiculos.id ? updatedVehiculos : i
        );
      } else {
        return [...prevVehiculos, updatedVehiculos];
      }
    });

    setOpen(false);      // para AgregarInfraccion
    setOpenEdit(false);  // para EditarInfraccion
  };

  // Open the dropdown menu for a row
  const handleMenuOpen = (event, vehiculo) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuVehiculo(vehiculo);  // Set selected vehicle
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuVehiculo(null);
  };

  const handleEditOpen = () => {
    setOpenEdit(true);
    setAnchorEl(null);
  };

  const handleRowClick = (vehiculo) => {
    setSelectedVehiculo(vehiculo);
  };

  const handleDetailClose = () => {
    setSelectedVehiculo(null);
  };

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search filtering (adapt fields as needed)
  const filterData = (array) => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return array.filter((vehiculo) =>
      (vehiculo.marca || "").toLowerCase().includes(lowercasedSearchTerm) ||
      (vehiculo.modelo || "").toLowerCase().includes(lowercasedSearchTerm) ||
      (vehiculo.patente || "").toLowerCase().includes(lowercasedSearchTerm) ||
      (vehiculo.tipo_vehiculo || "").toLowerCase().includes(lowercasedSearchTerm) ||
      (vehiculo.anio !== null && vehiculo.anio !== undefined ? vehiculo.anio.toString() : "").includes(lowercasedSearchTerm) ||
      (vehiculo.kilometraje !== null && vehiculo.kilometraje !== undefined ? vehiculo.kilometraje.toString() : "").includes(lowercasedSearchTerm)
    );
  };

  // Download all records as XLS
  const handleDownloadXLS = async () => {
    try {
      const response = await api.get("vehiculos/");
      const allData = response.data;

      const worksheet = XLSX.utils.json_to_sheet(allData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehiculos");

      XLSX.writeFile(workbook, "vehiculos.xlsx");
    } catch (error) {
      alert("Error al descargar los datos.");
      console.error(error);
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700} color="#152d51">
          Vehículos
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            variant="outlined"
            label="Buscar vehículo"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button onClick={handleDownloadXLS} variant="outlined" color="success">
            Descargar XLS&nbsp;<DownloadIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
          <Button
            onClick={() => setOpen(true)}
            style={{
              border: "1px solid #3f51b5",
              borderRadius: "6px",
              color: "#3f51b5",
              padding: "4px 10px",
              fontSize: "0.85rem"
            }}
          >
            Agregar Vehículo <AddIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Vehículos Table */}
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "rgb(244, 246, 248)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Marca</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Modelo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Dominio</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tipo de vehículo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Año</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Kilometraje</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filterData(vehiculos)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((vehiculo) => (
                <TableRow
                  key={vehiculo.id}
                  sx={{ '&:hover': { backgroundColor: '#f1f1f1' }, cursor: "pointer" }}
                  onClick={() => handleRowClick(vehiculo)}
                >
                  <TableCell>{vehiculo.marca}</TableCell>
                  <TableCell>{vehiculo.modelo}</TableCell>
                  <TableCell>{vehiculo.patente}</TableCell>
                  <TableCell>{vehiculo.tipo_vehiculo}</TableCell>
                  <TableCell>{vehiculo.anio}</TableCell>
                  <TableCell>{vehiculo.kilometraje} km</TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => handleMenuOpen(e, vehiculo)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={vehiculos.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
      />

      {/* Dropdown Menu for row actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditOpen}>
          <EditIcon color="primary" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(menuVehiculo.id); }}>
          <DeleteIcon color="error" sx={{ mr: 1 }} /> Eliminar
        </MenuItem>
      </Menu>

      {/* Detail Modal */}
      <Dialog open={!!selectedVehiculo} onClose={handleDetailClose} fullWidth maxWidth="sm">
        <DialogTitle>Detalles del Vehículo</DialogTitle>
        <DialogContent>
          {selectedVehiculo && (
            <DetalleVehiculo
              selectedVehiculo={selectedVehiculo}
              handleDetailClose={handleDetailClose}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose} color="secondary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <AgregarVehiculo handleClose={() => setOpen(false)} onUpdate={handleVehiculosUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Vehicle Modal (only one modal definition now) */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar Vehículo</DialogTitle>
        <DialogContent>
          {menuVehiculo ? (
            <EditarVehiculo vehiculo={menuVehiculo} handleClose={() => setOpenEdit(false)} onUpdate={handleVehiculosUpdated} />
          ) : (
            <Typography variant="h6" color="textSecondary">Cargando vehículo...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};

export default Vehiculos;
